import { 
  Car, 
  Users, 
  Shield, 
  Star, 
  MessageCircle, 
  Clock, 
  MapPin, 
  CheckCircle,
  Heart,
  Target,
  Lightbulb,
  Award,
  TrendingUp
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function About() {
  const values = [
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Innovation",
      description: "We embrace cutting-edge technology to revolutionize vehicle wash booking and customer experience.",
      color: "bg-yellow-100 text-yellow-600"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Community",
      description: "Building a strong ecosystem of customers and wash centers working together for mutual success.",
      color: "bg-blue-100 text-blue-600"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Trust & Security",
      description: "Ensuring secure transactions and reliable service delivery for all our users.",
      color: "bg-green-100 text-green-600"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Customer First",
      description: "Every decision we make is centered around providing the best experience for our users.",
      color: "bg-red-100 text-red-600"
    }
  ];

  const stats = [
    { number: "10K+", label: "Happy Customers", icon: <Users className="w-6 h-6" /> },
    { number: "500+", label: "Wash Centers", icon: <Car className="w-6 h-6" /> },
    { number: "50K+", label: "Washes Completed", icon: <CheckCircle className="w-6 h-6" /> },
    { number: "4.8", label: "Average Rating", icon: <Star className="w-6 h-6" /> }
  ];

  const team = [
    {
      name: "Rajesh Kumar",
      role: "Founder & CEO",
      description: "Visionary leader with 10+ years in automotive industry",
      avatar: "RK"
    },
    {
      name: "Priya Sharma",
      role: "CTO",
      description: "Tech expert passionate about innovative solutions",
      avatar: "PS"
    },
    {
      name: "Amit Patel",
      role: "Head of Operations",
      description: "Ensuring seamless service delivery across India",
      avatar: "AP"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Hero Section */}
      <div className="relative py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-green-600/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200 mb-6 inline-flex items-center max-w-full">
              <Heart className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="break-words">About Timelyyy</span>
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 break-words">
              Revolutionizing Vehicle Wash
              <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent"> Booking</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto break-words">
              We're on a mission to transform how India books and manages vehicle wash services. 
              Through innovative technology and WhatsApp integration, we're making car care 
              convenient, reliable, and accessible for everyone.
            </p>
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 justify-center max-w-2xl mx-auto">
              <Link to="/booking" className="w-full md:flex-1">
                <Button size="lg" className="w-full h-auto min-h-[52px] bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white px-6 sm:px-8 py-3.5 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2">
                  <Car className="w-5 h-5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Book Your Wash</span>
                </Button>
              </Link>
              <Link to="/onboard-vendor" className="w-full md:flex-1">
                <Button size="lg" variant="outline" className="w-full h-auto min-h-[52px] border-2 border-blue-200 text-blue-700 hover:bg-blue-50 px-6 sm:px-8 py-3.5 text-base sm:text-lg font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2">
                  <Users className="w-5 h-5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Join as Vendor</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4 text-white">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-0 shadow-xl bg-gradient-to-br from-blue-50 to-blue-100">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-3xl font-bold text-gray-900">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-lg text-gray-700 leading-relaxed">
                  To revolutionize the vehicle wash industry in India by connecting customers with 
                  premium wash centers through innovative technology, making car care convenient, 
                  reliable, and accessible for everyone while empowering wash center owners to grow their business.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl bg-gradient-to-br from-green-50 to-green-100">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-green-500 rounded-xl flex items-center justify-center mb-4">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-3xl font-bold text-gray-900">Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-lg text-gray-700 leading-relaxed">
                  To become India's leading vehicle wash booking platform, creating a seamless 
                  ecosystem where customers can easily find and book quality wash services, 
                  and wash centers can efficiently manage and grow their business through our platform.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Core Values */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These values guide everything we do and shape how we serve our customers and partners.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white">
                <CardHeader className="pb-4">
                  <div className={`w-16 h-16 rounded-xl ${value.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    {value.icon}
                  </div>
                  <CardTitle className="text-xl font-semibold text-gray-900">
                    {value.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    {value.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Passionate individuals working together to revolutionize vehicle wash booking in India.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    {member.avatar}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                  <p className="text-blue-600 font-semibold mb-3">{member.role}</p>
                  <p className="text-gray-600">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Choose Timelyyy?
              </h2>
              <p className="text-xl text-gray-600">
                We're not just another booking platform. We're your partner in transforming vehicle wash services.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">WhatsApp Integration</h3>
                    <p className="text-gray-600">Get booking confirmations, updates, and invoices directly on WhatsApp. No need to download additional apps.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Secure & Reliable</h3>
                    <p className="text-gray-600">Your payments are secure with our encrypted payment system. We ensure 100% data protection.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Growing Network</h3>
                    <p className="text-gray-600">Access to 500+ premium wash centers across India, with new centers joining every week.</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">24/7 Support</h3>
                    <p className="text-gray-600">Round-the-clock customer support to help you with any queries or issues.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Location-Based</h3>
                    <p className="text-gray-600">Find the nearest wash centers to your location with real-time availability and pricing.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Quality Assured</h3>
                    <p className="text-gray-600">All our partner wash centers are verified and rated by customers for quality service.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-blue-600 via-blue-700 to-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 break-words">
            Ready to Experience the Future of Vehicle Wash?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto break-words">
            Join thousands of satisfied customers and wash center owners who trust Timelyyy for their vehicle wash needs.
          </p>
          <div className="flex flex-col md:flex-row gap-3 md:gap-4 justify-center max-w-2xl mx-auto">
            <Link to="/booking" className="w-full md:flex-1">
              <Button size="lg" className="w-full h-auto min-h-[56px] bg-white text-blue-600 hover:bg-gray-100 px-6 sm:px-8 py-4 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2">
                <Car className="w-5 h-5 flex-shrink-0" />
                <span className="whitespace-nowrap">Book Your Wash Now</span>
              </Button>
            </Link>
            <Link to="/onboard-vendor" className="w-full md:flex-1">
              <Button size="lg" className="w-full h-auto min-h-[56px] bg-white text-blue-600 hover:bg-gray-100 px-6 sm:px-8 py-4 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2">
                <Users className="w-5 h-5 flex-shrink-0" />
                <span className="whitespace-nowrap">Join as Vendor</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
